import React, {useCallback, useEffect, useState} from 'react';
import {Button, Icon, message, Modal, Select, Table, Tooltip} from 'antd';
import {useTranslation} from 'react-i18next';
import ModalForm from './components/FormConfigModal';
import {FormDetail} from '../FormManager/components';
import {
    changeStatusFormPromise,
    createTemplatePromise,
    getTemplatesPromise,
    saveTemplateVersionPromise
} from './promises/formManagerPromises';
import './AdminFormManager.scss';
import VersionHistoryModal from "./components/VersionHistoryModal";
import moment from "moment/moment";
import formTypeValidator from "../../helpers/formTypeValidator";
import {CATEGORIAS, TIPOS_PERSONA} from "./constant";

const {Option} = Select;

const emptyFilters = {
    declarationType: '',
    category: '',
    personType: ''
};
const AdminFormManager = ({currentUser}) => {
    const {t} = useTranslation();
    const [dataTemplate, setDataTemplate] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalCreateEditVisible, setIsModalCreateEditVisible] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [versionModalVisible, setVersionModalVisible] = useState(false);
    const [versionModalTemplate, setVersionModalTemplate] = useState(null);
    const [filters, setFilters] = useState(emptyFilters);
    const [hasChanges, setHasChanges] = useState(false);

    const getTemplates = useCallback(() => {
        setLoading(true);
        getTemplatesPromise()
            .then((response) => {
                setDataTemplate(response);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        getTemplates();
    }, []);

    const closeModalPrivate = () => {
        setIsModalCreateEditVisible(false);
        setSelectedTemplateId(null);
        setIsEditMode(false);
        setLoading(false);
        handleHasChanges(false)
    }

    const handleCloseModal = () => {
        if(isEditMode && hasChanges) {
            Modal.confirm({
                title: t("messages.aml.confirm"),
                content: t('messages.aml.params.confirmation.leave'),
                okText: t('messages.aml.confirm'),
                cancelText: t('messages.aml.cancel'),
                onOk: () => {
                    closeModalPrivate()
                }
            });
        }else {
            closeModalPrivate()
        }
    }

    const handleCreateOrUpdateTemplate = useCallback((values, version, baseTemplate) => {
        setLoading(true);

        const payload = !isEditMode
            ? {
                tipoDeclaracion: values.tipoDeclaracion,
                categoria: values.categoria,
                tipoPersona: values.tipoPersona,
            }
            : {id: version.id, formId: selectedTemplateId, objJson: values};

        const action = !isEditMode
            ? () => createTemplatePromise(payload, baseTemplate ? baseTemplate.id : null)
            : () => saveTemplateVersionPromise(payload);

        Modal.confirm({
            title: !isEditMode ? t('messages.aml.form.template.new.template') : t("messages.aml.form.template.editButton"),
            content: !isEditMode ? (baseTemplate ? t('messages.aml.form.template.based.template') + baseTemplate.identificador
                    : t('messages.aml.form.template.new.version'))
                : t('messages.aml.form.template.update.version.content'),
            okText: t('messages.aml.confirm'),
            cancelText: t('messages.aml.cancel'),
            onOk: () => {
                action()
                    .then((r) => {
                        message.success(
                            !isEditMode
                                ? t('messages.aml.form.template.created.template.ok')
                                : t('messages.aml.form.template.update.version.ok')
                        );
                        getTemplates();
                        if (!isEditMode) {
                            setSelectedTemplateId(r)
                            setIsEditMode(true);
                        } else {
                            handleCloseModal();
                        }
                    })
                    .catch((e) => {
                        if (e.response.status === 409) {
                            message.error(t('messages.aml.form.template.error.template.exist'));
                        } else {
                            message.error(t('messages.aml.form.template.error.saving.template'));
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            },
        });


    }, [selectedTemplateId, isEditMode]);

    const handleAddNewTemplate = useCallback(() => {
        setSelectedTemplateId(null);
        setIsEditMode(false);
        setIsModalCreateEditVisible(true);
    }, []);

    const handleEditTemplate = useCallback((record) => {
        setSelectedTemplateId(record.id);
        setIsEditMode(true);
        setIsModalCreateEditVisible(true);
    }, []);

    const handleChangeStatus = useCallback((record) => {
        const isActive = record.active
        Modal.confirm({
            title: !isActive ? t('messages.aml.form.template.enable.tittle') : t('messages.aml.form.template.disable.tittle'),
            content: !isActive ? t('messages.aml.form.template.activate.template') : t('messages.aml.form.template.deactivate.template'),
            okText: t('messages.aml.confirm'),
            cancelText: t('messages.aml.cancel'),
            onOk: () => {
                record.active = !isActive;
                setLoading(true);
                changeStatusFormPromise(record)
                    .then(() => {
                        message.success(
                            t(`messages.aml.form.template.status.changed`)
                        );
                        getTemplates();
                    })
                    .catch(() => {
                        message.error(t('messages.aml.form.template.status.change.error'));
                    })
                    .finally(() => setLoading(false));
            },
        });
    }, [getTemplates, t]);

    const handleOpenVersionsModal = useCallback((record) => {
        setVersionModalTemplate(record);
        setVersionModalVisible(true);
    }, []);

    const handleCloseVersionModal = () => {
        setVersionModalVisible(false)
        setVersionModalTemplate(null);
        getTemplates()
    }

    const getTipesDeclarationOption = () => {
        let validTypes = formTypeValidator(currentUser, null, false);
        if (currentUser.modules.includes('ONBOARDING') && currentUser.cliente.modules.includes('ONBOARDING')) {
            validTypes = [...validTypes, "KYC"]
        }

        return validTypes.map(type => (
            <Option key={type} value={type}>
                {t(`messages.aml.type.${type}`)}
            </Option>
        ));
    }

    const handleFilterChange = (filterKey, value) => {
        setFilters({...filters, [filterKey]: value})
    }

    const selectedFilter = (filterType) => {
        if (filterType === "declarationType") {
            if (filters.declarationType === "") {
                return t('messages.aml.form.template.tipo.declaracion');
            } else {
                return filters.declarationType;
            }
        } else if (filterType === "category") {
            if (filters.category === "") {
                return t('messages.aml.category');
            } else {
                return filters.category;
            }
        } else {
            if (filters.personType === "") {
                return t('messages.aml.form.template.tipo.persona');
            } else {
                return filters.personType;
            }
        }


    }

    const filteredData = dataTemplate.filter(item => {
        const matchDeclaration = !filters.declarationType || item.tipoDeclaracion === filters.declarationType;
        const matchCategory = !filters.category || item.categoria === filters.category;
        const matchPersonType = !filters.personType || item.tipoPersona === filters.personType;

        return matchDeclaration && matchCategory && matchPersonType;
    });

    const columns = [
        {
            title: 'ID',
            dataIndex: 'identificador',
            key: 'identificador',
            width: 60,
            align: 'center',
            sorter: (a, b) => a.identificador - b.identificador,
        },
        {
            title: <Select
                size='small'
                style={{width:200}}
                placeholder={t("messages.aml.form.template.tipo.declaracion")}
                value={selectedFilter("declarationType")}
                onChange={(value) => handleFilterChange('declarationType', value)}
            >
                <Option value="">{t('messages.aml.inside.all.content')}</Option>
                {getTipesDeclarationOption()}

            </Select>,
            dataIndex: 'tipoDeclaracion',
            render: (text) => t('messages.aml.type.' + text),
            width: 210,
        },
        {
            title: (
                <Select
                    size='small'
                    style={{width:150}}
                    placeholder={t("messages.aml.category")}
                    value={selectedFilter("category")}
                    onChange={(value) => handleFilterChange('category', value)}
                >
                    <Option value="">{t('messages.aml.inside.all.content')}</Option>
                    {CATEGORIAS.map((category) => (
                        <Option key={category} value={category}>
                            {t('messages.aml.category.' + category)}
                        </Option>
                    ))}
                </Select>
            ),
            dataIndex: 'categoria',
            render: (val) => t('messages.aml.category.' + val),
            width: 170
        },
        {
            title: (
                <Select
                    size='small'
                    style={{width:150}}
                    placeholder={t("messages.aml.form.template.tipo.persona")}
                    value={selectedFilter("personType")}
                    onChange={(value) => handleFilterChange('personType', value)}
                >
                    <Option value="">{t('messages.aml.inside.all.content')}</Option>
                    {TIPOS_PERSONA.map((personType) => (
                        <Option key={personType} value={personType}>
                            {t('messages.aml.typePerson.' + personType)}
                        </Option>
                    ))}
                </Select>
            ),
            dataIndex: 'tipoPersona',
            render: (val) => t('messages.aml.typePerson.' + val),
            width: 170,
        },
        {
            title: t('messages.aml.form.template.currentVersion'),
            dataIndex: 'version',
            align: 'center',
            render: (version, record) => (
                <Tooltip title={t('messages.aml.form.template.available.version')}>
                    <a onClick={() => handleOpenVersionsModal(record)}>{version}</a>
                </Tooltip>
            ),
        },

        {
            title: t('messages.aml.register.table.titles.lastUpdate'),
            align: 'center',
            key: 'lastVersionInfo',
            dataIndex: 'lastVersionInfo',
            sorter: (a, b) => new Date(a.lastVersionInfo?.fecha || 0) - new Date(b.lastVersionInfo?.fecha || 0),
            render: (info) => (
                <div style={{ textAlign: 'center'}}>
                    {info?.autor}
                    <br />
                    <small>{info?.fecha ? moment(info.fecha).format('DD-MM-YYYY HH:mm:ss') : '-'}</small>
                </div>
            ),
            width: 200,
        },

        {
            title: t('messages.aml.form.template.acciones'),
            key: 'acciones',
            align: 'center',
            sorter: (a, b) => {
                return (a.active === b.active) ? 0 : a.active ? -1 : 1;
            },
            render: (text, record) => (
                <div style={{display: 'flex', gap: '8px'}}>
                    <Button
                        icon="eye"
                        size="small"
                        onClick={() => handleOpenVersionsModal(record)}
                    >
                        {t('messages.aml.detail')}
                    </Button>
                    {record.active ? (
                            <Button
                                type="danger"
                                icon="block"
                                size="small"
                                disabled={currentUser.type === 'AUDIT'}
                                onClick={() => handleChangeStatus(record)}
                            >
                                {t('messages.aml.form.template.disable')}
                            </Button>
                        ) :
                        <div className="active-button-wrapper">

                            <Tooltip title={t('messages.aml.form.template.enable.message')}>

                                <Button
                                    type="default"
                                    icon="check"
                                    className="btn-enable"
                                    size="small"
                                    disabled={currentUser.type === 'AUDIT'}
                                    onClick={() => handleChangeStatus(record)}>
                                    {t('messages.aml.form.template.enable')}
                                </Button>
                            </Tooltip>
                        </div>
                    }
                </div>
            ),
        },
    ];

    const clearFilters= () => {
        setFilters(emptyFilters);
    }

    const handleHasChanges = (changes) => {
        setHasChanges(changes)
    }

    return (
        <div className="admin-form-manager-container">

            <div className="tools-area">
                &nbsp;
                <Button
                    disabled={currentUser.type === 'AUDIT'}
                    type="primary"
                    onClick={handleAddNewTemplate}
                    icon={<Icon type="plus"/>}>
                    {t('messages.aml.form.template.new.template')}
                </Button>
                &nbsp;
                <Button type="primary" icon="close" onClick={ clearFilters }>{ t('messages.aml.clearAllFilters') }</Button>

            </div>

            <Table
                size="small"
                columns={columns}
                dataSource={filteredData}
                loading={loading}
                rowClassName={(record) => !record.active ? 'disabled-row' : ''}
            />

            {isModalCreateEditVisible && (
                !isEditMode ?
                    <ModalForm
                        currentUser={currentUser}
                        onOk={handleCreateOrUpdateTemplate}
                        onCancel={handleCloseModal}
                        dataTemplate={dataTemplate}
                    />
                    :
                    <Modal
                        visible={true}
                        title={t('messages.aml.form.template.edit.template')}
                        onCancel={handleCloseModal}
                        footer={<Button type="primary" onClick={handleCloseModal}>{t('messages.aml.form.template.close', {ns: 'forms'})}</Button>}
                        width={"97vw"}
                        bodyStyle={{minHeight: 'calc(100vh - 120px)', overflowY: 'auto'}}
                        style={{top: "5px"}}
                    >
                        <FormDetail currentUser={currentUser} templateId={selectedTemplateId}
                                    handleSaveTemplate={handleCreateOrUpdateTemplate} handleHasChanges={handleHasChanges} />
                    </Modal>
            )}

            {versionModalVisible &&
                <VersionHistoryModal
                    currentUser={currentUser}
                    template={versionModalTemplate}
                    onClose={handleCloseVersionModal}
                    handleEditTemplate={handleEditTemplate}
                />
            }
        </div>
    );
};

export default AdminFormManager;
